import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, ArrowRight, MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { BRAND, NAV_LINKS, SERVICES } from "@/data/site";
import { subscribeNewsletter } from "@/services/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're subscribed. Welcome to the cadence.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative bg-[#0d1c40] text-white overflow-hidden">
      <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#2FA86A]/20 blur-3xl" />
      <div className="nc-container relative pt-20 pb-10">
        {/* Newsletter */}
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] items-end pb-14 border-b border-white/10">
          <div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.12]">
              Build your next chapter,<br /> one cadence at a time.
            </h3>
            <p className="mt-4 text-white/60 max-w-md">
              Career insights, interview strategies, and job market intelligence — no noise, just signal.
            </p>
          </div>
          {/* <form onSubmit={onSubscribe} data-testid="newsletter-form" className="w-full">
            <div className="flex items-center gap-2 rounded-full bg-white/10 border border-white/15 p-1.5 focus-within:border-[#2FA86A] transition-colors">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 bg-transparent px-4 py-2.5 text-white placeholder-white/40 outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#2FA86A] px-5 py-2.5 font-semibold text-white transition-colors hover:bg-[#248a5a] disabled:opacity-60"
              >
                {loading ? "..." : <>Subscribe <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </form> */}
        </div>

        {/* Columns */}
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4 py-14">
          <div>
            <img src={BRAND.logoLight} alt="North Cadence" className="h-24 w-auto" />
            <p className="mt-5 text-white/60 leading-relaxed text-sm">
              A career growth and talent services firm — the rhythm of a career, intentionally built.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Linkedin, href: BRAND.social.linkedin, label: "LinkedIn" },
                { Icon: Instagram, href: BRAND.social.instagram, label: "Instagram" },
                { Icon: Facebook, href: BRAND.social.facebook, label: "Facebook" },
              ].map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${i}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#2FA86A] hover:text-[#2FA86A]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white/90 mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/60 hover:text-white transition-colors text-sm">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white/90 mb-5">Services</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.title}>
                  <Link to="/services" className="text-white/60 hover:text-white transition-colors text-sm">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white/90 mb-5">Get in touch</h4>
            <ul className="space-y-4 text-sm text-white/60">
              <li className="flex gap-3"><MapPin className="h-5 w-5 shrink-0 text-[#2FA86A]" /><span>{BRAND.locations.join(" · ")}</span></li>
              <li className="flex gap-3"><Mail className="h-5 w-5 shrink-0 text-[#2FA86A]" /><a href={`mailto:${BRAND.email}`} className="hover:text-white">{BRAND.email}</a></li>
              <li className="flex gap-3"><Phone className="h-5 w-5 shrink-0 text-[#2FA86A]" /><a href={`tel:${BRAND.phone}`} className="hover:text-white">{BRAND.phone}</a></li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-4 pt-8 border-t border-white/10 text-sm text-white/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p>© {new Date().getFullYear()} North Cadence. All rights reserved.</p>
            <p>The Rhythm Of A Career, Intentionally Built.</p>
          </div>
          <p className="text-center">
            Developed by{" "}
            <a
              href="https://www.instagram.com/brandistaaa?stkn=bTNvNzM2NnQ5ZjM3&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-white/80 hover:text-white transition-colors"
            >
              Brandistaaa
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
